import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Phone, Mail, AlertTriangle, Check, Trash2, Calendar, Clock, Plus, CalendarClock } from "lucide-react";
import { RescheduleDialog } from "@/components/RescheduleDialog";
import { api, fmtMoney, apiError, fmtTimeStr, fmtHour } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { CustomerPicker } from "@/components/CustomerPicker";
import { DoorCode } from "@/components/DoorCode";
import { CancelBookingDialog } from "@/components/CancelBookingDialog";
import { Calendar as DayCalendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STATUSES = ["pending_payment", "booked", "completed", "cancelled"];
const STATUS_COLOR = {
  pending_payment: "text-amber-400 border-amber-400/30",
  booked: "text-green-400 border-green-400/30",
  completed: "text-blue-400 border-blue-400/30",
  cancelled: "text-red-400 border-red-400/30",
  slot_conflict: "text-orange-400 border-orange-400/40",
};

const keyOf = (b) => `${b.event_date || ""}T${String(b.start_time ?? 0).padStart(2, "0")}`;
// Upcoming sessions soonest-first, then past sessions most-recent-first.
function byUpcoming(a, b) {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const ua = (a.event_date || "") >= today, ub = (b.event_date || "") >= today;
  if (ua !== ub) return ua ? -1 : 1;
  return ua ? keyOf(a).localeCompare(keyOf(b)) : keyOf(b).localeCompare(keyOf(a));
}

export default function Dashboard() {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const [filter, setFilter] = useState("all");
  const { data: bookings = [], isLoading } = useQuery({ queryKey: ["admin-bookings"], queryFn: () => api.get("/bookings").then((r) => r.data) });

  const filtered = bookings.filter((b) => {
    if (filter === "all") return true;
    if (filter === "requests") return b.change_request && !b.change_request.resolved;
    return b.status === filter;
  }).sort(byUpcoming);

  const [cancelling, setCancelling] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const patch = async (id, body) => {
    try {
      await api.patch(`/bookings/${id}`, body);
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
      toast.success(body.status === "cancelled" ? (body.notify ? "Booking cancelled — customer notified" : "Booking cancelled") : "Updated");
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  const resolve = async (id) => {
    try {
      await api.post(`/bookings/${id}/resolve-request`);
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
      toast.success("Request resolved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
      toast.success("Deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const pendingRequests = bookings.filter((b) => b.change_request && !b.change_request.resolved).length;
  const [showNew, setShowNew] = useState(false);

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-white mb-1">Bookings</h1>
          <p className="text-white/50 text-sm">{bookings.length} total · {bookings.filter((b) => b.payment_status === "paid").length} paid</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-booking-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2"><Plus size={15} /> New Booking</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "requests", ...STATUSES].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`booking-filter-${f}`}
            className={`px-4 py-1.5 rounded-full text-xs uppercase font-mono-label tracking-wide transition-colors ${filter === f ? "btn-gold" : "border border-white/15 text-white/60 hover:border-white/40"}`}
          >
            {f.replace("_", " ")}{f === "requests" && pendingRequests > 0 ? ` (${pendingRequests})` : ""}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-white/40">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-white/40">No bookings.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((b, i) => (
            <div key={b.id} className="bg-[#111] border border-white/10 rounded-2xl p-5" data-testid={`admin-booking-${i}`}>
              {b.change_request && !b.change_request.resolved && (
                <div className="flex flex-wrap items-center justify-between gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-2.5 mb-4">
                  <p className="text-amber-300 text-xs flex items-center gap-2">
                    <AlertTriangle size={14} /> {b.change_request.type.toUpperCase()} requested: {b.change_request.message || "(no message)"}
                  </p>
                  <div className="flex items-center gap-3 shrink-0">
                    {b.change_request.type === "reschedule" && <button onClick={() => setRescheduling(b)} data-testid={`request-reschedule-${i}`} className="text-xs text-amber-300 hover:text-white flex items-center gap-1"><CalendarClock size={13} /> Reschedule</button>}
                    <button onClick={() => resolve(b.id)} data-testid={`resolve-request-${i}`} className="text-xs text-amber-300 hover:text-white flex items-center gap-1"><Check size={13} /> Resolve</button>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-lg text-white">{b.name}</h3>
                    <span className={`text-[10px] uppercase border rounded-full px-2 py-0.5 ${STATUS_COLOR[b.status] || "text-white/60 border-white/20"}`}>{(b.status || "").replace("_", " ")}</span>
                    <span className={`text-[10px] uppercase rounded-full px-2 py-0.5 ${b.payment_status === "paid" ? "bg-green-500/20 text-green-400" : "bg-white/10 text-white/50"}`}>{b.payment_status}{b.payment_status === "paid" && b.total_cents != null ? ` · ${fmtMoney(b.total_cents)}` : ""}</span>
                  </div>
                  <p className="text-white/60 text-sm mt-1.5">{b.service_title}</p>
                  <p className="text-white/40 text-xs mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Calendar size={12} /> {b.event_date}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {fmtTimeStr(b.event_time)} · {b.hours}h</span>
                  </p>
                  {b.equipment?.length > 0 && <p className="text-white/40 text-xs mt-1">Add-ons: {b.equipment.map((e) => e.name).join(", ")}</p>}
                  {b.sms_consent ? (
                    <p className="text-green-400/80 text-xs mt-1" data-testid={`sms-consent-${i}`} title={b.sms_consent_text || ""}>✓ SMS opt-in {b.sms_consent_at ? `· ${new Date(b.sms_consent_at).toLocaleString()}` : ""}</p>
                  ) : (
                    <p className="text-white/30 text-xs mt-1" data-testid={`sms-consent-${i}`}>No SMS opt-in</p>
                  )}
                  <div className="flex flex-wrap gap-x-3 gap-y-2 mt-3">
                    <a href={`tel:${b.phone}`} data-testid={`call-${i}`} className="text-xs flex items-center gap-1.5 text-white/60 hover:text-[#D4AF37]"><Phone size={13} /> {b.phone}</a>
                    <a href={`mailto:${b.email}`} data-testid={`email-${i}`} className="text-xs flex items-center gap-1.5 text-white/60 hover:text-[#D4AF37]"><Mail size={13} /> {b.email}</a>
                    <DoorCode booking={b} canManage={admin?.role === "super_admin"} onChange={() => qc.invalidateQueries({ queryKey: ["admin-bookings"] })} testid={`door-code-${i}`} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={b.status} onValueChange={(v) => (v === "cancelled" ? setCancelling(b) : patch(b.id, { status: v }))}>
                    <SelectTrigger data-testid={`status-select-${i}`} className="w-40 bg-[#0a0a0a] border-white/15 text-white text-xs h-9"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {b.status !== "cancelled" && <button onClick={() => setRescheduling(b)} title="Reschedule" data-testid={`reschedule-booking-${i}`} className="text-white/40 hover:text-[#D4AF37] p-2"><CalendarClock size={16} /></button>}
                  <button onClick={() => remove(b.id)} data-testid={`delete-booking-${i}`} className="text-white/40 hover:text-red-400 p-2"><Trash2 size={16} /></button>
                </div>
              </div>
              {b.project_details && <p className="text-white/40 text-xs mt-3 border-t border-white/5 pt-3">{b.project_details}</p>}
            </div>
          ))}
        </div>
      )}

      <RescheduleDialog booking={rescheduling} onClose={() => setRescheduling(null)} onDone={() => qc.invalidateQueries({ queryKey: ["admin-bookings"] })} />
      <CancelBookingDialog booking={cancelling} onClose={() => setCancelling(null)} onConfirm={async (body) => { if (await patch(cancelling.id, body)) setCancelling(null); }} />
      <NewBookingDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => qc.invalidateQueries({ queryKey: ["admin-bookings"] })} />
    </div>
  );
}

const EMPTY_BOOKING = {
  first_name: "", last_name: "", email: "", phone: "", dob: "", project_details: "",
  service_id: "", event_date: "", start_time: 10, hours: 1, equipment: [], promo_code: "",
  payment_status: "paid", payment_provider: "manual", send_confirmation: true, sms_consent: false,
};

function NewBookingDialog({ open, onClose, onCreated }) {
  const { admin } = useAuth();
  const isSuper = admin?.role === "super_admin";
  const blank = isSuper ? EMPTY_BOOKING : { ...EMPTY_BOOKING, payment_status: "unpaid", payment_provider: "" };
  const [f, setF] = useState(blank);
  const [picked, setPicked] = useState(null);
  const [showCal, setShowCal] = useState(false);
  const pickCustomer = (c) => { setPicked(c); setF((x) => ({ ...x, first_name: c.first_name || "", last_name: c.last_name || "", email: c.email || "", phone: c.phone || "", dob: c.dob || "" })); };
  const clearCustomer = () => { setPicked(null); setF((x) => ({ ...x, first_name: "", last_name: "", email: "", phone: "", dob: "" })); };
  const [saving, setSaving] = useState(false);
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data), enabled: open, staleTime: 0, refetchOnMount: "always" });
  const { data: equip = [] } = useQuery({ queryKey: ["equipment"], queryFn: () => api.get("/equipment").then((r) => r.data), enabled: open });

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleEquip = (id) => setF((x) => ({ ...x, equipment: x.equipment.includes(id) ? x.equipment.filter((e) => e !== id) : [...x.equipment, id] }));

  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email || !f.service_id || !f.event_date) {
      toast.error("Name, email, service and date are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/bookings", {
        ...f,
        dob: f.dob || null,
        promo_code: f.promo_code || null,
        start_time: Number(f.start_time),
        hours: Number(f.hours),
      });
      toast.success("Booking created");
      onCreated();
      setF(blank);
      setPicked(null);
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Could not create booking"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111] border-white/15 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">New Booking (on behalf of customer)</DialogTitle>
          <DialogDescription className="text-white/50">{isSuper ? "Create a manual booking for a customer. Total is computed automatically." : "Create a manual booking for a customer. It stays unpaid until the super admin marks it paid."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <CustomerPicker picked={picked} onPick={pickCustomer} onClear={clearCustomer} />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="First name *" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-[#0a0a0a] border-white/15" data-testid="nb-first" />
            <Input placeholder="Last name *" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-[#0a0a0a] border-white/15" data-testid="nb-last" />
          </div>
          <Input placeholder="Email *" value={f.email} onChange={(e) => set("email", e.target.value)} className="bg-[#0a0a0a] border-white/15" data-testid="nb-email" />
          <Input placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0a0a0a] border-white/15" data-testid="nb-phone" />

          <Select value={f.service_id} onValueChange={(v) => set("service_id", v)}>
            <SelectTrigger data-testid="nb-service" className="bg-[#0a0a0a] border-white/15 text-white"><SelectValue placeholder="Select service *" /></SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
              {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.title} {isSuper && s.hourly_rate > 0 ? `($${s.hourly_rate}/hr)` : ""}</SelectItem>)}
            </SelectContent>
          </Select>

          <div>
            <label className="text-white/50 text-[10px] uppercase font-mono-label">Date *</label>
            <button type="button" onClick={() => setShowCal((v) => !v)} data-testid="nb-date"
              className="mt-1 w-full h-10 flex items-center gap-2 rounded-md border border-white/15 bg-[#0a0a0a] px-3 text-sm text-left">
              <Calendar size={15} className="text-[#D4AF37]" />
              <span className={f.event_date ? "text-white" : "text-white/40"}>
                {f.event_date ? format(parseISO(f.event_date), "EEEE, MMM d, yyyy") : "Tap to pick a date"}
              </span>
            </button>
            {showCal && (
              <div className="mt-2 rounded-lg border border-white/10 bg-[#0a0a0a] flex justify-center" data-testid="nb-date-calendar">
                <DayCalendar mode="single" selected={f.event_date ? parseISO(f.event_date) : undefined}
                  onSelect={(d) => { if (d) { set("event_date", format(d, "yyyy-MM-dd")); setShowCal(false); } }} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/50 text-[10px] uppercase font-mono-label">Start</label>
              <Select value={String(f.start_time)} onValueChange={(v) => set("start_time", v)}>
                <SelectTrigger data-testid="nb-start" className="bg-[#0a0a0a] border-white/15 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/15 text-white max-h-60">
                  {Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-[10px] uppercase font-mono-label">Hours</label>
              <Input type="number" min={1} value={f.hours} onChange={(e) => set("hours", e.target.value)} className="bg-[#0a0a0a] border-white/15" data-testid="nb-hours" />
            </div>
          </div>

          {equip.length > 0 && (
            <div>
              <label className="text-white/50 text-[10px] uppercase font-mono-label mb-1 block">Add-ons</label>
              <div className="flex flex-wrap gap-2">
                {equip.map((e) => (
                  <button key={e.id} type="button" onClick={() => toggleEquip(e.id)} data-testid={`nb-addon-${e.id}`} className={`text-xs rounded-full px-3 py-1.5 border ${f.equipment.includes(e.id) ? "border-[#D4AF37] bg-[#161310] text-white" : "border-white/15 text-white/60"}`}>
                    {e.name}{isSuper ? ` +${fmtMoney(Math.round(e.price * 100))}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isSuper && <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Promo code (optional)" value={f.promo_code} onChange={(e) => set("promo_code", e.target.value)} className="bg-[#0a0a0a] border-white/15" data-testid="nb-promo" />
            <Input placeholder="Payment method (e.g. cash, card, zelle)" value={f.payment_provider} onChange={(e) => set("payment_provider", e.target.value)} className="bg-[#0a0a0a] border-white/15" data-testid="nb-provider" />
          </div>}

          <Textarea placeholder="Project details (optional)" value={f.project_details} onChange={(e) => set("project_details", e.target.value)} className="bg-[#0a0a0a] border-white/15" rows={2} />

          {isSuper && <div className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2.5">
            <span className="text-sm text-white/80">Mark as paid now</span>
            <Switch checked={f.payment_status === "paid"} onCheckedChange={(v) => set("payment_status", v ? "paid" : "unpaid")} data-testid="nb-paid" />
          </div>}
          <div className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2.5">
            <span className="text-sm text-white/80">Email confirmation to customer</span>
            <Switch checked={f.send_confirmation} onCheckedChange={(v) => set("send_confirmation", v)} data-testid="nb-confirm" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2.5">
            <span className="text-sm text-white/80">Customer gave SMS opt-in consent</span>
            <Switch checked={f.sms_consent} onCheckedChange={(v) => set("sms_consent", v)} data-testid="nb-sms" />
          </div>

          <button onClick={submit} disabled={saving} data-testid="nb-submit" className="btn-gold w-full rounded-full py-3 text-sm font-mono-label uppercase disabled:opacity-60">
            {saving ? "Creating…" : "Create Booking"}
          </button>
          {isSuper && <p className="text-white/35 text-[11px] text-center">Total is computed automatically from service rate × hours + add-ons − promo.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
